/**
 * alertRuleManagement.ts
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: Shared type definitions for alert rule CRUD operations.
 * These types are shared between frontend and backend.
 */

// =============================================================================
// Core Domain Types
// =============================================================================

/**
 * Alert types supported by the system
 */
/**
 * Spec 193: `alert_type` is an OPEN display label (the engine selects its adapter from
 * condition_json.resource_type, not alert_type). Kept as a string alias so any engine
 * alert type displays/creates without a 400. The legacy backup values live in
 * ALERT_TYPE_OPTIONS for the (now-optional) label dropdown.
 */
export type AlertType = string;

/**
 * Spec 193: canonical resource_type registry — the adapter key stored in
 * `alert_rules.condition_json.resource_type`. One value per v1 engine adapter
 * (the 11 GROUNDED adapters; `logging-engine-health` is deferred to Spec 196 and
 * intentionally absent). `alert_type` remains a free display label (open
 * namespace); the resource_type is what selects the engine's condition adapter.
 */
export type ResourceType =
  | 'pipeline'
  | 'capacity'
  | 'capacity-telemetry-staleness'
  | 'backup-run'
  | 'backup-readiness'
  | 'collection-job'
  | 'storage-threshold'
  | 'recoverability'
  | 'restore-evidence'
  | 'predictability'
  | 'immutability';

/**
 * Severity levels for alerts. Spec 193 adds Warning/Info (the engine's severities);
 * High/Medium/Low retained for legacy Feature-125 rules.
 */
export type Severity = 'Critical' | 'Warning' | 'Info' | 'High' | 'Medium' | 'Low';

/**
 * Notification channel types
 */
export type NotificationChannelType = 'Email' | 'Teams' | 'Webhook';

/**
 * Single notification channel configuration
 */
export interface NotificationChannel {
  type: NotificationChannelType;
  destination: string;
}

/**
 * Condition JSON structure (flexible - these are required fields)
 */
export interface ConditionJson {
  field: string;
  operator: string;
  value: unknown;
  [key: string]: unknown; // Allow extension
}

// =============================================================================
// Entity Types
// =============================================================================

/**
 * Alert rule entity as stored in database
 */
export interface AlertRuleEntity {
  id: string;
  name: string;
  alertType: AlertType;
  conditionJson: ConditionJson;
  lakehouseId: string | null;
  enabled: boolean;
  severityOverride: Severity | null;
  notificationChannels: NotificationChannel[] | null;
  cooldownMinutes: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Alert rule with joined lakehouse name for display
 */
export interface AlertRuleView extends AlertRuleEntity {
  lakehouseName: string | null;
  /** Spec 192: 'Global' (aggregation-path, zero assignments) | 'Unassigned' (per-target,
   *  zero assignments — inert) | lakehouse name (single legacy target) | 'N capacities, M lakehouses' */
  scope: 'Global' | 'Unassigned' | string;
}

// =============================================================================
// Spec 192 — Assignment management (junction targeting)
// =============================================================================

export interface CapacityAssignment {
  capacityId: string;
  capacityName: string | null;
  lastTriggeredAt: string | null;
  assignedAt: string;
}

export interface LakehouseAssignment {
  lakehouseId: string;
  lakehouseName: string | null;
  lastTriggeredAt: string | null;
  assignedAt: string;
}

export interface RuleAssignments {
  ruleId: string;
  scope: 'global' | 'scoped' | 'unassigned';
  capacities: CapacityAssignment[];
  lakehouses: LakehouseAssignment[];
}

/** POST body — exactly one key. */
export interface AddAssignmentRequest {
  capacityId?: string;
  lakehouseId?: string;
}

// =============================================================================
// API Request/Response Types
// =============================================================================

/**
 * Filter options for listing alert rules
 */
export interface AlertRuleFilters {
  alertType?: AlertType;
  severity?: Severity;
  enabled?: boolean;
  search?: string; // Partial match on name
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page: number; // 1-based
  pageSize: number; // 10, 25, 50
}

/**
 * Sort parameters
 */
export interface SortParams {
  sortBy: 'name' | 'alertType' | 'severityOverride' | 'enabled' | 'createdAt' | 'updatedAt';
  sortOrder: 'asc' | 'desc';
}

/**
 * Paginated list response
 */
export interface AlertRuleListResponse {
  data: AlertRuleView[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/**
 * Request body for creating a new alert rule
 */
export interface CreateAlertRuleRequest {
  name: string;
  alertType: AlertType;
  conditionJson: ConditionJson;
  lakehouseId?: string | null;
  enabled?: boolean;
  severityOverride?: Severity | null;
  notificationChannels?: NotificationChannel[] | null;
  cooldownMinutes?: number;
}

/**
 * Request body for updating an existing alert rule
 */
export interface UpdateAlertRuleRequest {
  name?: string;
  alertType?: AlertType;
  conditionJson?: ConditionJson;
  lakehouseId?: string | null;
  enabled?: boolean;
  severityOverride?: Severity | null;
  notificationChannels?: NotificationChannel[] | null;
  cooldownMinutes?: number;
}

/**
 * Request body for toggling alert rule status
 */
export interface ToggleAlertRuleRequest {
  enabled: boolean;
}

/**
 * Name validation request
 */
export interface ValidateNameRequest {
  name: string;
  excludeId?: string; // For edit mode - exclude current rule from uniqueness check
}

/**
 * Name validation response
 */
export interface ValidateNameResponse {
  isValid: boolean;
  message?: string;
}

// =============================================================================
// Reference Data Types
// =============================================================================

/**
 * Select option for dropdowns
 */
export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Alert type option with default severity
 */
export interface AlertTypeOption extends SelectOption {
  defaultSeverity: Severity;
}

/**
 * Severity option with color coding
 */
export interface SeverityOption extends SelectOption {
  color: 'red' | 'orange' | 'yellow' | 'blue';
  priority: number;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Alert type options for dropdowns
 */
export const ALERT_TYPE_OPTIONS: AlertTypeOption[] = [
  { value: 'BackupFailed', label: 'Backup Failed', description: 'Backup operation failed', defaultSeverity: 'High' },
  { value: 'BackupOverdue', label: 'Backup Overdue', description: 'Backup not run within threshold', defaultSeverity: 'Medium' },
  { value: 'RestoreFailed', label: 'Restore Failed', description: 'Restore operation failed', defaultSeverity: 'Critical' },
  { value: 'ValidationFailed', label: 'Validation Failed', description: 'Data validation check failed', defaultSeverity: 'High' },
];

/**
 * Option metadata for a resource_type (Spec 193). `targetEntityTypes` feeds the
 * universal rule-form scope picker (which `alert_rule_junction.entity_type`
 * values this resource type may target). Empty target list ⇒ aggregation/global
 * rule only (no per-entity targeting).
 */
export interface ResourceTypeOption {
  value: ResourceType;
  label: string;
  description: string;
  targetEntityTypes: string[];
}

/** Spec 193: the 11 grounded v1 adapters, in universal-form display order. */
export const RESOURCE_TYPE_OPTIONS: ResourceTypeOption[] = [
  { value: 'pipeline', label: 'Pipeline', description: 'Data-movement process failed / stuck / stale / partial', targetEntityTypes: ['pipeline'] },
  { value: 'capacity', label: 'Capacity', description: 'Fabric capacity throttling / utilization threshold', targetEntityTypes: ['capacity'] },
  { value: 'capacity-telemetry-staleness', label: 'Capacity Telemetry Staleness', description: 'Capacity telemetry not refreshed within its window', targetEntityTypes: ['capacity'] },
  { value: 'backup-run', label: 'Backup Run', description: 'Backup run Failed / Partial / Refused', targetEntityTypes: ['lakehouse', 'warehouse'] },
  { value: 'backup-readiness', label: 'Backup Readiness', description: 'Predicted backup readiness blocked / at-risk', targetEntityTypes: ['lakehouse', 'warehouse', 'workspace'] },
  { value: 'collection-job', label: 'Collection Job', description: 'Collection tier failed / stuck / stale', targetEntityTypes: ['collection_job'] },
  { value: 'storage-threshold', label: 'Storage Threshold', description: 'Storage-account utilization breach', targetEntityTypes: ['storage_account'] },
  { value: 'recoverability', label: 'Recoverability', description: 'Recoverability verification failed / at-risk', targetEntityTypes: ['lakehouse', 'warehouse'] },
  { value: 'restore-evidence', label: 'Restore Evidence', description: 'Restore run completed without recorded evidence (terminal)', targetEntityTypes: ['restore_run'] },
  { value: 'predictability', label: 'Predictability', description: 'Open predictability finding on a monitored process', targetEntityTypes: ['pipeline'] },
  { value: 'immutability', label: 'Immutability', description: 'Backup expected to be WORM-locked failed to lock', targetEntityTypes: ['lakehouse', 'warehouse'] },
];

/**
 * Severity options for dropdowns
 */
export const SEVERITY_OPTIONS: SeverityOption[] = [
  { value: 'Critical', label: 'Critical', color: 'red', priority: 1 },
  { value: 'Warning', label: 'Warning', color: 'yellow', priority: 2 }, // Spec 193 engine severity
  { value: 'Info', label: 'Info', color: 'blue', priority: 3 }, // Spec 193 engine severity
  { value: 'High', label: 'High', color: 'orange', priority: 4 },
  { value: 'Medium', label: 'Medium', color: 'yellow', priority: 5 },
  { value: 'Low', label: 'Low', color: 'blue', priority: 6 },
];

/**
 * Notification channel type options
 */
export const NOTIFICATION_CHANNEL_OPTIONS: SelectOption[] = [
  { value: 'Email', label: 'Email', description: 'Send email notification' },
  { value: 'Teams', label: 'Microsoft Teams', description: 'Send Teams webhook message' },
  { value: 'Webhook', label: 'Webhook', description: 'Call external webhook URL' },
];

/**
 * Default values for new alert rules
 */
export const ALERT_RULE_DEFAULTS = {
  enabled: true,
  cooldownMinutes: 60,
  notificationChannels: [],
} as const;

/**
 * Page size options
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

/**
 * Condition JSON examples per alert type (for help panel)
 */
export const CONDITION_EXAMPLES: Record<AlertType, ConditionJson> = {
  BackupFailed: {
    field: 'status',
    operator: 'equals',
    value: 'failed',
  },
  BackupOverdue: {
    field: 'lastBackupHours',
    operator: 'greaterThan',
    value: 24,
  },
  RestoreFailed: {
    field: 'status',
    operator: 'equals',
    value: 'failed',
  },
  ValidationFailed: {
    field: 'validationStatus',
    operator: 'equals',
    value: 'failed',
  },
};
